import { useMemo } from 'react';
import { programmeTypes } from '@constants/index';
import { getCommaSeparatedUIDs } from '@utils/textHelper';
import { getSubscription } from '@models/Me';

import {
  START_DATE,
  END_DATE,
  EDUCATOR_UID,
  EDUCATOR_USERNAME,
  TEST_SERIES_UID,
  TEST_SERIES_TITLE,
  COURSE_TITLE,
  COURSE_UID,
  BATCH_TITLE,
  BATCH_UID,
  IS_ENROLLED,
  LANGUAGE,
  GOAL_NAME,
  GOAL_UID,
  IS_SUBSCRIPTION_ACTIVE,
  LAST_PRIMARY_SOURCE_SECTION,
  IS_STORE_EXCLUSIVE,
  EDUCATOR_TYPE,
  BATCH_TYPE,
  CURRENT_PAGE_URL,
  ADDITIONAL_FEATURES,
  IS_SELLABLE
} from '@events/segmentProps';
import { getMePlusSubscriptions } from '@api/selectors/subscription';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import {
  EDUCATOR_TYPES,
  getBatchType,
  getIsStoreModule,
  getIsStoreSyllabus
} from '@cont/Batch/utils';
import { useRouter } from 'next/router';

const useAllTypesOfCourseEventProps = ({
  course = {},
  isTestSeries = false,
  isBatch = false
}) => {
  const plusSubscriptions = useMyInfo(getMePlusSubscriptions);
  const router = useRouter();
  const { asPath } = router || {};

  return useMemo(() => {
    const {
      startsAt,
      endsAt,
      name: courseTitle,
      uid: courseUID,
      authors,
      courseType,
      completedAt,
      isEnrolled,
      language,
      goal,
      isStoreExclusive,
      educatorType,
      contentType,
      additionalFeatures,
      isSellable
    } = course || {};
    const { uid: goalUID, name: goalName } = goal || {};
    const { time_remaining = 0 } =
      getSubscription(plusSubscriptions, goalUID) ?? {};
    const is_subscription_active = time_remaining > 0;

    const segmentData = {
      [START_DATE]: startsAt,
      [END_DATE]: endsAt || completedAt,
      [EDUCATOR_UID]: getCommaSeparatedUIDs(authors),
      [EDUCATOR_USERNAME]: getCommaSeparatedUIDs(authors, 'username'),
      [IS_ENROLLED]: isEnrolled || false,
      [LANGUAGE]: language || getCommaSeparatedUIDs(language, 'label'),
      [GOAL_NAME]: goalName,
      [GOAL_UID]: goalUID,
      [IS_SUBSCRIPTION_ACTIVE]: is_subscription_active,
      [LAST_PRIMARY_SOURCE_SECTION]: 'NA',
      [IS_STORE_EXCLUSIVE]: isStoreExclusive,
      [EDUCATOR_TYPE]:
        educatorType === EDUCATOR_TYPES.PARTNER ? 'Partner' : 'Plus'
    };
    if (isBatch) {
      const isModuleFeatureAvailable = getIsStoreModule({ additionalFeatures });
      const isSyllabusFeatureAvailable = getIsStoreSyllabus({
        additionalFeatures
      });
      let batchFeature = '';
      if (isModuleFeatureAvailable) {
        batchFeature = 'Module';
      }
      if (isSyllabusFeatureAvailable) {
        batchFeature = 'Syllabus';
      }
      segmentData[BATCH_UID] = courseUID;
      segmentData[BATCH_TITLE] = courseTitle;
      segmentData[BATCH_TYPE] = getBatchType(contentType);
      segmentData[CURRENT_PAGE_URL] = asPath;
      segmentData[ADDITIONAL_FEATURES] = batchFeature;
      segmentData[IS_SELLABLE] = isSellable;
    } else if (courseType === programmeTypes.TESTSERIES || isTestSeries) {
      segmentData[TEST_SERIES_UID] = courseUID;
      segmentData[TEST_SERIES_TITLE] = courseTitle;
    } else {
      segmentData[COURSE_UID] = courseUID;
      segmentData[COURSE_TITLE] = courseTitle;
    }
    return segmentData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course, isBatch, isTestSeries, asPath]);
};

export default useAllTypesOfCourseEventProps;
